import React from "react";
import { Controller } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import { ProductStoreCategory } from "../../api/fetchProductStoreCategories/types";
import { CreateProductStoreCategoryRequest } from "../../api/createProductStoreCategory/types";
import { ColorOption } from "../../api/fetchProductStoreCategoryMetadata/types";
import { useCategoryForm } from "../../hooks/useCategoryForm";

interface CategoryFormProps {
  initialData?: ProductStoreCategory | null;
  onSubmit: (data: CreateProductStoreCategoryRequest) => Promise<void>;
  onCancel: () => void;
  isLoading: boolean;
  availableColors: ColorOption[];
}

const CategoryForm: React.FC<CategoryFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isLoading,
  availableColors,
}) => {
  const { form, isEditing, submit } = useCategoryForm({
    initialData,
    onSubmit,
  });
  const {
    register,
    control,
    formState: { errors },
  } = form;

  return (
    <form className="space-y-6" onSubmit={submit}>
      <div className="space-y-4">
        <div>
          <label
            className="block text-sm font-medium mb-1 text-gray-900 dark:text-white"
            htmlFor="name"
          >
            Name *
          </label>
          <Input id="name" placeholder="Category name" {...register("name")} />
          {errors.name && (
            <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
          )}
        </div>

        <div>
          <label
            className="block text-sm font-medium mb-1 text-gray-900 dark:text-white"
            htmlFor="description"
          >
            Description
          </label>
          <Input
            id="description"
            placeholder="Category description (optional)"
            {...register("description")}
          />
        </div>

        <div>
          <label
            className="block text-sm font-medium mb-1 text-gray-900 dark:text-white"
            htmlFor="colorId"
          >
            Color *
          </label>
          <Controller
            control={control}
            name="colorId"
            render={({ field }) => (
              <Select
                onValueChange={(val) => field.onChange(Number(val))}
                value={field.value ? String(field.value) : undefined}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a color" />
                </SelectTrigger>
                <SelectContent>
                  {availableColors.map((color) => {
                    if (color.id == null || color.value == null) {
                      return null;
                    }
                    return (
                      <SelectItem key={color.id} value={String(color.id)}>
                        <span
                          className="mr-2 inline-block h-3 w-3 rounded-full"
                          style={{ backgroundColor: color.value }}
                        />
                        {color.value}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            )}
          />
          {errors.colorId && (
            <p className="text-red-500 text-sm mt-1">
              {errors.colorId.message}
            </p>
          )}
        </div>
      </div>

      <div className="flex justify-end space-x-2">
        <Button
          disabled={isLoading}
          onClick={onCancel}
          type="button"
          variant="outline"
        >
          Cancel
        </Button>
        <Button disabled={isLoading} type="submit">
          {isLoading ? "Saving..." : isEditing ? "Save" : "Create"}
        </Button>
      </div>
    </form>
  );
};

export default CategoryForm;
